import type {
  CashierPaymentMethod,
  FeeSettingsResponse,
  PaymentMethodConfigurationNetworkSettingsBase,
} from "@/api/cashier";
import { EnumFeeType } from "@/api/cashier";
import { moneyToMajorUnits } from "@/utils/helpers";

export function toFeeDisplay(
  fee?: FeeSettingsResponse,
  defaultCurrencyCode?: string
): FeeSettingsResponse | null | undefined {
  if (!fee) return undefined;
  if (fee.type === EnumFeeType.EnumFeeTypePercentage) {
    return { ...fee, type: "percentage" };
  }
  const feeValue = moneyToMajorUnits(fee.value);
  if (feeValue == null) return undefined;
  const feeCurrencyCode = fee.value.code || defaultCurrencyCode;

  if (feeValue === 0) return null; // explicitly fee-free → "No fee"
  if (fee.type === EnumFeeType.EnumFeeTypeFixed) {
    // currency_code is required to render a fixed fee — omit badge if missing
    if (!feeCurrencyCode && feeValue === 0) return null;
    if (!feeCurrencyCode && feeValue > 0) return undefined;
    return { ...fee, type: "fixed" };
  }
  return undefined; // unknown type — omit badge rather than mislabel
}

export type DisplaySettings = Pick<
  PaymentMethodConfigurationNetworkSettingsBase,
  "expected_processing_time" | "info_message"
>;

export function buildRefundMethodSubTextFromSettings(
  detail: string,
  settings?: DisplaySettings
): (string | { processingTime?: string; infoMessage?: string })[] {
  const result: (string | { processingTime?: string; infoMessage?: string })[] =
    [detail];
  const processingTime = settings?.expected_processing_time;
  const infoMessage = settings?.info_message;
  if (processingTime || infoMessage) {
    result.push({ processingTime, infoMessage });
  }
  return result;
}

export function selectRefundDisplaySettings(
  pm?: CashierPaymentMethod
): DisplaySettings | undefined {
  if (!pm) return undefined;
  if (!("refund" in pm.base_settings) || !("payout" in pm.base_settings)) {
    return undefined;
  }

  return pm.base_settings.refund.enabled
    ? pm.base_settings.refund
    : pm.base_settings.payout;
}

export function buildRefundMethodSubText(
  detail: string,
  pm?: CashierPaymentMethod
): (string | { processingTime?: string; infoMessage?: string })[] {
  const settings = selectRefundDisplaySettings(pm);
  return buildRefundMethodSubTextFromSettings(detail, settings);
}
